'use client';

import { useGridPage } from '@/hooks/use-grid-page';
import {
  isLiveGridId as isLiveGridIdShape,
  resolveLobbyLiveGridId,
} from '@/features/lobby/data/boardroom-grid';
import { useTutorialOptional } from '@/features/tutorial/TutorialContext';
import { TUTORIAL_BOARD_ID } from '@/features/tutorial/tutorial-board';

/** Re-export for back-compat with any external import sites. */
export const isLiveLobbyGridId = isLiveGridIdShape;

/**
 * Single call site for the lobby's live grid data fetch.
 *
 * `useGridPage` opens its own SSE / live-grid connections and runs its
 * own forfeit-timer / refetch effects. Calling it from multiple
 * consumer hooks (one per panel) caused 5+ simultaneous SSE
 * connections to the same `poolId` and an effect feedback loop that
 * tripped React's "Maximum update depth exceeded" guard whenever a
 * live board became active.
 *
 * This wrapper guarantees the lobby calls `useGridPage` exactly once
 * per active board id. The full `useGridPage` return value is then
 * threaded down to `useLobbySeats`, `useLobbyAdapters` and
 * `useLobbyRewards` so each can pick the slice it needs without
 * spinning up its own duplicate fetch.
 *
 * The pinned `'boardroom'` id is translated via `resolveLobbyLiveGridId`
 * into the live composite id of HV grid 0, so the flagship board
 * shares the same live-data plumbing as every community grid. When
 * the HV env vars aren't wired (early SSR / unit tests) we pass the
 * `disabled-0` sentinel — `useGridPage` short-circuits all network /
 * SSE work for ids matching `disabled-*`.
 */
export function useLobbyGridPage(activeBoardId: string | undefined) {
  const tutorial = useTutorialOptional();
  // For the tutorial board we still let `useGridPage('disabled-0')` run so
  // hook order stays constant. The downstream lobby hooks
  // (`useLobbySeats`, `useLobbyAdapters`, `useLobbyRewards`) check the
  // tutorial context themselves and replace the surface they consume.
  const liveId = resolveLobbyLiveGridId(activeBoardId);
  const effectiveId =
    tutorial && activeBoardId === TUTORIAL_BOARD_ID
      ? 'disabled-tutorial'
      : (liveId ?? 'disabled-0');
  return useGridPage(effectiveId);
}

export type LobbyGridPage = ReturnType<typeof useLobbyGridPage>;
